import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Tracking events are shown in the local time of the place where they
// happened, with the zone labeled (airline / Cargo iQ convention), never in
// the viewer's zone.

export interface LocalEventTime {
  /** "02 nov 2026" */
  date: string;
  /** "02 nov" */
  shortDate: string;
  /** "16:30", empty for date-only values */
  time: string;
  /** "GMT-3", "UTC" when the place's zone is unknown, empty for date-only values */
  zone: string;
  /** Local calendar day, "2026-11-02" */
  day: string;
}

const pad = (value: number) => String(value).padStart(2, '0');

const isValidTimeZone = (timeZone: string | null | undefined) => {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
};

// Values without an explicit offset (e.g. a planned "2026-11-03") are plain
// calendar dates: keep their day as written.
const hasExplicitOffset = (value: string) =>
  /(?:[zZ]|[+-]\d{2}(?::?\d{2})?)$/.test(value) && value.includes('T');

export const getLocalEventTime = (
  value: string | null | undefined,
  timeZone?: string | null
): LocalEventTime | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  if (!hasExplicitOffset(trimmed)) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
    if (!match) return null;
    const local = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );
    if (Number.isNaN(local.getTime())) return null;
    return {
      date: format(local, 'dd MMM yyyy', { locale: es }),
      shortDate: format(local, 'dd MMM', { locale: es }),
      time: '',
      zone: '',
      day: `${match[1]}-${match[2]}-${match[3]}`,
    };
  }

  const instant = new Date(trimmed);
  if (Number.isNaN(instant.getTime())) return null;

  const knownZone = isValidTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: knownZone ? (timeZone as string) : 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'shortOffset',
  }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((item) => item.type === type)?.value);
  const year = part('year');
  const month = part('month');
  const day = part('day');

  return {
    date: format(new Date(year, month - 1, day), 'dd MMM yyyy', { locale: es }),
    shortDate: format(new Date(year, month - 1, day), 'dd MMM', { locale: es }),
    time: `${pad(part('hour'))}:${pad(part('minute'))}`,
    // Some engines print London in winter as "GMT+0"; keep it "GMT".
    zone: knownZone
      ? (
          parts.find((item) => item.type === 'timeZoneName')?.value || 'UTC'
        ).replace(/^GMT[+-]0$/, 'GMT')
      : 'UTC',
    day: `${year}-${pad(month)}-${pad(day)}`,
  };
};

/** "02 nov 2026 · 16:30 GMT-3" with time, "02 nov 2026" without. */
export const formatLocalEventTime = (
  value: string | null | undefined,
  timeZone: string | null | undefined,
  withTime: boolean
) => {
  const local = getLocalEventTime(value, timeZone);
  if (!local) return '—';
  return withTime && local.time
    ? `${local.date} · ${local.time} ${local.zone}`
    : local.date;
};

/** Local calendar day ("2026-11-02") of a value at a place, or the value unchanged. */
export const getLocalEventDay = (
  value: string | null | undefined,
  timeZone?: string | null
) => getLocalEventTime(value, timeZone)?.day ?? value ?? null;
